import XUIButton from '@xero/xui/react/button';
import XUIPickitem from '@xero/xui/react/components/picklist/XUIPickitem';
import XUIPicklistDivider from '@xero/xui/react/components/picklist/XUIPicklistDivider';
import XUILoader from '@xero/xui/react/loader';
import XUIPanel from '@xero/xui/react/panel';
import Fuse from 'fuse.js';
import { ChangeEvent, FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import logos from '../../assets/logos';
import { DropDownSelect } from '../../components/DropDownSelect';
import { setVisible } from '../../helpers/header';
import useDidMountEffect from '../../helpers/hooks';
import { IOrganisation } from '../../models';
import { IShopifySettingsResponse } from '../../models/shopify';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { getOrganisationsAction, organisationSelectors } from '../../redux/slices/OrganisationSlice';
import { getSettingsAction, settingsSelectors } from '../../redux/slices/SettingsSlice';
import { useAppDispatch } from '../../redux/store';
import { integrationTypes } from '../../static/constants/integrationTypes';
import lang from '../../static/lang/en-NZ';
import './index.css';
import InformationBanner from '../../components/InformationBanner/index';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

export interface IOrganisationProps {
    setOrganisation: (organisation: IOrganisation) => void;
    onClose: () => void;
    invalidOrgId: string | null;
}

export const urlRegex = /(?=[\&\?]shop\=(?<shopname>.+\.myshopify\.com))/im;

export const OrganisationPicker: FC<IOrganisationProps> = ({ setOrganisation, onClose, invalidOrgId }) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const organisation = useSelector(organisationSelectors.organisation);
    const organisations = useSelector(organisationSelectors.organisations);
    const settings = useSelector(settingsSelectors.settings);
    const settingsError = useSelector(settingsSelectors.error);

    const [selectedOrg, setSelectedOrg] = useState<IOrganisation | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const initSettingsRef = useRef(false);

    const integrationLang: any = lang[installation.integrationType];
    const text = integrationLang.ORGANISATION;

    useEffect(() => {
        const shouldInitSettings = !settings.installationId && !initSettingsRef.current;

        if (shouldInitSettings) {
            dispatch(getSettingsAction());
        }

        initSettingsRef.current = true;
    }, [dispatch, settings.installationId]);

    useDidMountEffect(() => {
        if (installation.integrationType === integrationTypes.SHOPIFY) {
            const shopifySettings = { ...settings } as IShopifySettingsResponse;
            const redirectShop = urlRegex.exec(window.location.search)?.groups?.shopname;
            if (installation.isOnboarded && redirectShop && redirectShop === shopifySettings.shop) {
                // Assuming that bootstrap is always called before this given that for this to be true we have context.installation.isOnboarded
                setOrganisation(organisation);
                return;
            }
        }

        // There is an installation for this tenant and with this installationId but it does not match
        // with the shop we are getting redirected from
        dispatch(getOrganisationsAction());
    }, [settings, settingsError]);

    const fuse = useMemo(
        () =>
            new Fuse(organisations, {
                keys: ['name'],
            }),
        [organisations]
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);

    const fuzzySearchResult = useMemo(() => fuse.search(searchTerm).map((m) => m.item), [searchTerm, fuse]);

    useEffect(() => {
        (() => {
            if (invalidOrgId) {
                const invalidOrg = organisations.find((o) => o.id === invalidOrgId) ?? null;
                setSelectedOrg(invalidOrg);
            }

            if (hasOnlyOneSelectableOrg()) {
                setOrganisation(organisations[0]);
            }
        })();
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organisations]);

    useLayoutEffect(() => setVisible(false), []);

    const isValidOrg = (org: IOrganisation) => {
        return org.hasInstallation || org.userIsAdmin;
    };

    const getValidOrganisations = (fuzzyResult: IOrganisation[]) => {
        return fuzzyResult.length > 0
            ? orgFilter(fuzzyResult, (org) => isValidOrg(org))
            : orgFilter(organisations, (org) => isValidOrg(org));
    };

    const isAdminRequiredOrg = (org: IOrganisation) => {
        return !org.userIsAdmin && !isValidOrg(org);
    };

    const getAdminRequiredOrganisations = (fuzzyResult: IOrganisation[]) => {
        return fuzzyResult.length > 0
            ? orgFilter(fuzzyResult, (org) => isAdminRequiredOrg(org))
            : orgFilter(organisations, (org) => isAdminRequiredOrg(org));
    };

    const hasOnlyOneSelectableOrg = () => {
        const validOrgs = getValidOrganisations([]);
        return (
            organisations.length === 1 &&
            validOrgs.length === 1 &&
            invalidOrgId === null &&
            !validOrgs[0].hasInstallation
        );
    };

    function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
        return value !== null && value !== undefined;
    }

    const orgFilter = (orgs: IOrganisation[], filter: (org: IOrganisation) => boolean | undefined) => {
        const mapped = orgs.map((org) => {
            if (filter(org)) {
                return org;
            }
            return null;
        });
        const filtered: IOrganisation[] = mapped.filter(notEmpty);
        return filtered;
    };

    if (!organisations.length || hasOnlyOneSelectableOrg()) {
        return <XUILoader ariaLabel='Loading' />;
    }

    const errorBanner = () => {
        let bannerMessage;
        if (
            organisations.length === 1 &&
            getValidOrganisations([]).length === 1 &&
            invalidOrgId === null &&
            getValidOrganisations([])[0].hasInstallation
        ) {
            bannerMessage = text.ONLY_ORG_ALREADY_CONNECTED_ERROR_MESSAGE;
        }
        return (
            bannerMessage && (
                <InformationBanner bannerMessage={bannerMessage} sentiment={'negative'} displayCloseButton={true} />
            )
        );
    };

    return (
        <div className='organisation xui-u-flex xui-u-flex-justify-center xui-margin-horizontal-auto xui-margin-vertical-5xlarge xui-u-flex-align-center'>
            <XUIPanel className='organisation--panel' qaHook='organisation-page'>
                <div className='xui-text-align-center'>
                    <img src={logos.XERO} alt='Xero' />
                    <p className='xui-font-size-Large'>
                        {text.HEADING}
                        <b>{text.INTEGRATION_NAME}</b>:
                    </p>
                </div>

                <hr className='xui-margin-top-large xui-margin-bottom-large section-separator' />

                {errorBanner()}

                <DropDownSelect
                    buttonContent={selectedOrg?.name ?? text.DROPDOWN_SELECT}
                    showSearchBox={true}
                    onInputChange={handleSearch}
                    className='organisation-dropdown'
                    onBlur={() => setSearchTerm('')}
                    qaHook='organisation-dropdown'
                    isInvalid={selectedOrg?.id === invalidOrgId}
                    validationMessage={text.VALIDATION_MESSAGE}
                >
                    {getValidOrganisations(fuzzySearchResult).map((m, i) => (
                        <XUIPickitem
                            key={i}
                            id={'organisation-' + m.id}
                            isSelected={selectedOrg?.id === m.id}
                            value={m}
                            isDisabled={!m.userIsAdmin || m.hasInstallation}
                            onClick={() => setSelectedOrg(m)}
                        >
                            {m.name}
                        </XUIPickitem>
                    ))}
                    {getAdminRequiredOrganisations(fuzzySearchResult).length > 0 && (
                        <>
                            <XUIPicklistDivider />
                            <div className='xui-padding-vertical-small xui-padding-horizontal list-sub-heading xui-pickitem--secondary'>
                                {text.ADMIN_PERMISSIONS_REQUIRED}
                            </div>
                            {getAdminRequiredOrganisations(fuzzySearchResult).map((m, i) => (
                                <XUIPickitem
                                    key={i}
                                    id={'organisation-' + m.id}
                                    isSelected={false}
                                    value={m}
                                    isDisabled={true}
                                >
                                    {m.name}
                                </XUIPickitem>
                            ))}
                        </>
                    )}
                </DropDownSelect>
                <hr className='xui-margin-top-large xui-margin-bottom-large section-separator' />
                <div className='organisation--buttons xui-u-flex'>
                    <XUIButton
                        qaHook='organisation-select--primaryAction'
                        className='xui-actions--primary'
                        onClick={() => setOrganisation(selectedOrg!)}
                        isDisabled={!selectedOrg || selectedOrg?.id === invalidOrgId}
                        variant='main'
                    >
                        {text.CONNECT_ORG}
                    </XUIButton>
                    <XUIButton
                        qaHook='organisation-select--secondaryAction'
                        className='xui-actions--secondary xui-margin-top'
                        onClick={() => onClose()}
                    >
                        {text.CANCEL_CONNECT}
                    </XUIButton>
                </div>
            </XUIPanel>
        </div>
    );
};

export default withLDConsumer()(OrganisationPicker);
